import type { FC } from 'react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CwsCheckbox,
  CwsDivider,
  CwsIcon,
  CwsInput,
  CwsP,
  CwsSection,
  CwsSpan,
  CwsGridRow,
  CwsGridCol,
  CwsButton,
  CwsH3,
  CwsLabelSet,
} from '@components/cws'
import * as R from 'ramda'
import { compose, find, propEq, propOr, uniqWith } from 'ramda'
import { Input } from '@ui/components'
import { Section } from './Section'
import s from './CategorySidePanel.module.scss'
import { SpoilerChevron } from '@ui/icons/common'
import type { CategoryFacet, CustomFacetValue, ManufacturerFacet } from '@model/product'
import type { BaseOption } from '@ui/components/Dropdown/Dropdown'
import { Search } from '@ui/icons/button'
import type { InputChangeHandler } from '@ui/components/Input/Input'
import { PriceRangeSlider as RangeSlider } from '@components/common/PriceRangeSlider'
import type { CustomFacet } from '@model/product/CustomFacet'
import { CategoryList } from '@components/category/CategoryList/CategoryList'
import { useCategoryFilters } from '@context/category-filters'
import { useDebouncedValue } from '@hooks/useDebounce'
import { useTranslate } from '@hooks/useTranslation'
import { isGloballyPricelist } from '@utils/pricelist-utils/app-properties'
import { FilterHeading } from '@components/category/CategorySidePanel/FilterHeading'
import type { CatalogFromContent } from '@api/content'
import dynamic from 'next/dynamic'
import { useMediaLess } from '@ui/hooks/useMediaLess'
import { MOBILE_BP } from '@app/constants'
import { useRouter } from 'next/router'
import { findPopularKeyForLabel } from '@utils/findPopularKeyForLabel'
import { useInitialData } from '@context/initialDataContext'
import { MOBIL_SIZE_FILTER, SidePanelWrapper } from '@components/category/CategorySidePanel/SidePanelWrapper'
// @ts-ignore
import { modal } from 'cms-web-styleguide/dist/utils/utils'

const ShipFilter = dynamic(() => import('@components/category/ShipFilter'), {
  loading: () => null,
})

export const MAX_MANUFACTURER_FACETS = 12

export interface CategorySidePanelProps {
  manufacturerFacets: ManufacturerFacet[]
  customFacets: CustomFacet[]
  categoryFacets: CategoryFacet[]
  categoryId?: number
  collectionName?: string
  minAvailablePrice: number
  currentMinPrice: number
  currentMaxPrice: number
  maxAvailablePrice: number
  loading?: boolean
  noShadow?: boolean
  catalogGroups?: Record<string, CatalogFromContent[]>
  resetAll: () => void
}

interface ManufacturerCheckItemProps {
  manufacturer: ManufacturerFacet
  checked?: boolean
  onCheck?: (manufacturer: ManufacturerFacet) => any
}

const ManufacturerCheckItem: FC<ManufacturerCheckItemProps> = (props) => {
  const { manufacturer, onCheck, checked = false } = props
  const { name, numberOfProducts } = manufacturer

  return (
    <CwsGridCol col="12">
      <CwsGridRow>
        <CwsGridCol col="fluid">
          <CwsCheckbox
            id={`brand-filter-checkbox-${manufacturer.id}`}
            checked={checked}
            onChange={onCheck?.bind(this, manufacturer)}
          >
            {name}
          </CwsCheckbox>
        </CwsGridCol>
        <CwsGridCol col="auto">
          {!!(numberOfProducts ?? 1) && (
            <CwsSpan
              className="bg-group-grey leading-5.5 px-[8px] py-[2px] rounded-[2px] text-midnight text-sm font-medium cursor-pointer"
              color="midnight"
            >
              {numberOfProducts}
            </CwsSpan>
          )}
        </CwsGridCol>
      </CwsGridRow>
    </CwsGridCol>
  )
}

interface FacetCheckItemProps {
  name: string
  count: number
  checked?: boolean
  onCheck?: (name: string) => any
}

const FacetCheckItem: FC<FacetCheckItemProps> = (props) => {
  const { name, count, onCheck, checked = false } = props
  return (
    <CwsGridCol col="12">
      <CwsGridRow>
        <CwsGridCol col="fluid">
          <CwsCheckbox id={`custom-facet-checkbox-${name}`} checked={checked} onChange={onCheck?.bind(this, name)}>
            {name}
          </CwsCheckbox>
        </CwsGridCol>
        <CwsGridCol col="auto">
          {!!(count ?? 1) && (
            <CwsSpan
              className="bg-group-grey leading-5.5 px-[8px] py-[2px] rounded-[2px] text-midnight text-sm font-medium cursor-pointer"
              color="midnight"
            >
              {count}
            </CwsSpan>
          )}
        </CwsGridCol>
      </CwsGridRow>
    </CwsGridCol>
  )
}
const SectionBreak: FC = () => {
  return <CwsDivider variant="solid" />
}
const CategorySidePanel: FC<CategorySidePanelProps> = (props) => {
  const {
    categoryId,
    minAvailablePrice,
    maxAvailablePrice,
    currentMaxPrice,
    currentMinPrice,
    loading = false,
    noShadow = false,
    catalogGroups = {},
    resetAll,
  } = props

  const [manufacturerFacets, setManufacturerFacets] = useState(props.manufacturerFacets)
  const [customFacets, setCustomFacets] = useState(props.customFacets)
  const {
    initialData: { tagGroups },
  } = useInitialData()
  const isMobile = useMediaLess(MOBIL_SIZE_FILTER)

  const { translate } = useTranslate()
  const { pathname, locale } = useRouter()
  const hideBrandsFilter = useMemo(
    () => pathname.startsWith('/brands') || pathname.startsWith('/[catalogId]/brands/'),
    [pathname]
  )

  const getFilterDescription = useCallback(
    (facetNumber: number, defaultName: string): string => {
      // @ts-ignore
      return compose(propOr(defaultName, 'description'), find(propEq('facetNumber', facetNumber)))(tagGroups)
    },
    [tagGroups]
  )

  useEffect(() => {
    if (loading) {
      return
    }

    setManufacturerFacets(props.manufacturerFacets)
    setCustomFacets(props.customFacets)
  }, [props.manufacturerFacets, props.customFacets, loading])

  const [categoryFacets, setCategoryFacets] = useState(props.categoryFacets)

  const campaigns = customFacets?.find((facet) => facet.number === 9)?.values ?? []
  const campaignsNames = campaigns?.map((campaign) => campaign.name) ?? []
  const [checkedCampaignsNames, setCheckedCampaignsNames] = useState<Record<string, boolean>>(
    campaignsNames?.reduce((map, campaignName) => ({ [campaignName]: false }), {}) ?? {}
  )

  const productLines = customFacets?.find((facet) => facet.number === 8)?.values ?? []
  const originCountries = customFacets?.find((facet) => facet.number === 10)?.values ?? []
  const productSpecifications = customFacets?.find((facet) => facet.number === 11)?.values ?? []
  const customLabels = customFacets?.find((facet) => facet.number === 16)?.values ?? []

  useEffect(() => {
    if (loading) {
      return
    }

    setCategoryFacets(props.categoryFacets)
  }, [props.categoryFacets, loading])

  const [expandedManufacturers, setExpandedManufacturers] = useState(false)

  const lastTwoManufacturers = manufacturerFacets.slice(manufacturerFacets.length - 2)
  const searchBrandPlaceholder = `${lastTwoManufacturers.map((manufacturer) => manufacturer.name).join(', ')}, ...`

  const [searchBrandValue, setSearchBrandValue] = useState('')

  const searchBrandsResult = useMemo(() => {
    return manufacturerFacets.filter((manufacturer) => {
      const manufacturerName = manufacturer.name.toLowerCase()
      const searchValue = searchBrandValue
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]/gi, '')
        .replace(/(\ )+/gi, ' ')

      return (
        manufacturerName.includes(searchValue) ||
        manufacturerName.replace(/[^a-z0-9]/gi, '').includes(searchValue) ||
        manufacturerName.replace(/[^a-z0-9]/gi, ' ').includes(searchValue)
      )
    })
  }, [manufacturerFacets, searchBrandValue])

  const searchBrandsResultOptions = useMemo<BaseOption[]>(() => {
    return searchBrandsResult.map<BaseOption>((brand) => ({ key: brand.id.toString(), name: brand.name }))
  }, [searchBrandsResult])

  const {
    brands: brandsFilters,
    encodeBrandFilter,
    toggleBrandFilter,
    isBrandFilterEnabled,
    customs,
    encodeCustomFilter,
    isCustomFilterEnabled,
    toggleCustom,
    queryParams,
    minPrice,
    maxPrice,
    setMinPrice,
    setMaxPrice,
    ...categoryFilters
  } = useCategoryFilters()

  const brands = brandsFilters.map((b) => b.query)

  const brandIds = useMemo(() => {
    return brandsFilters.map((b) => parseInt(b.id))
  }, [brandsFilters])

  const checkedBrands = useMemo(() => {
    return brandIds.map((bid) => bid.toString())
  }, [brandIds])

  const sortFacetsByNumberOfProducts = useCallback((facets: ManufacturerFacet[]) => {
    return facets.sort((a, b) => a.numberOfProducts - b.numberOfProducts).reverse()
  }, [])

  const sortFacetsByAlphabet = useCallback((facets: ManufacturerFacet[]) => {
    const alphabeticalSorting = R.sortBy(R.prop('name'), facets)
    return alphabeticalSorting
  }, [])

  const takeMaxFacetsToRender = useCallback((facets: ManufacturerFacet[]) => {
    return facets.slice(0, MAX_MANUFACTURER_FACETS)
  }, [])

  const manufacturerFacetsToRender = useMemo(() => {
    const uniqByBrandId = uniqWith((b1: ManufacturerFacet, b2) => b1.id === b2.id)
    const sortedFacets = [
      ...sortFacetsByAlphabet(manufacturerFacets.filter((m) => checkedBrands.includes(m.id.toString()))),
      ...sortFacetsByAlphabet(manufacturerFacets.filter((m) => !checkedBrands.includes(m.id.toString()))),
    ]

    if (searchBrandValue) {
      return uniqByBrandId(
        expandedManufacturers
          ? sortFacetsByAlphabet(searchBrandsResult)
          : takeMaxFacetsToRender(sortFacetsByAlphabet(searchBrandsResult ?? []))
      )
    }
    return uniqByBrandId(
      expandedManufacturers ? sortFacetsByAlphabet(manufacturerFacets) : takeMaxFacetsToRender(sortedFacets)
    )
  }, [
    manufacturerFacets,
    expandedManufacturers,
    searchBrandValue,
    searchBrandsResult,
    checkedBrands,
    sortFacetsByAlphabet,
    takeMaxFacetsToRender,
  ])
  const sortedSizeChart: { [kay: string]: string } = {
    'facet.tallink_size.xxs': '1',
    'facet.size.xxs': '1',
    'facet.tallink_size.xs': '2',
    'facet.size.xs': '2',
    'facet.tallink_size.s': '3',
    'facet.size.s': '3',
    'facet.tallink_size.m': '4',
    'facet.size.m': '4',
    'facet.tallink_size.l': '5',
    'facet.size.l': '5',
    'facet.tallink_size.xl': '6',
    'facet.size.xl': '6',
    'facet.tallink_size.xxl': '7',
    'facet.size.xxl': '7',
    'facet.tallink_size.xxxl': '8',
    'facet.size.xxxl': '8',
    'facet.tallink_size.xxxxl': '9',
    'facet.size.xxxxl': '9',
  }

  const customFacetsToRender = useMemo(() => {
    const sortByAlphabetCustomFacets = customFacets.map((customFacet) => {
      const val = customFacet.values.map((val) => ({
        ...val,
        nameForSort:
          customFacet.name === 'Size' ? sortedSizeChart[val.name] ?? '9' : translate(findPopularKeyForLabel(val.name)),
      }))

      const values = val.sort((a, b) => {
        return a.nameForSort.localeCompare(b.nameForSort, locale, { sensitivity: 'base' })
      })
      const valuesNameList = values.reduce((acc, val) => {
        if (acc[val.nameForSort]) {
          acc[val.nameForSort].push(val)
          return { ...acc }
        }
        return { ...acc, [val.nameForSort]: [val] }
      }, {} as Record<string, { nameForSort: string; name: string; number: number }[]>)

      const gruoup: { nameForSort: string; name: string; number: number }[] = Object.keys(valuesNameList).map(
        (item) => {
          if (valuesNameList[item].length > 1) {
            return {
              name: valuesNameList[item].map(({ name }) => name).join('&&'),
              nameForSort: item,
              number: R.sum(valuesNameList[item].map(({ number }) => number)),
            }
          }
          return valuesNameList[item][0]
        }
      )

      return { ...customFacet, values: gruoup }
    })

    return sortByAlphabetCustomFacets
  }, [customFacets, translate])

  const [minPriceState, setMinPriceState] = useState<number | null>(minPrice)
  const [maxPriceState, setMaxPriceState] = useState<number | null>(maxPrice)

  useEffect(() => {
    minPrice && setMinPriceState(minPrice)
  }, [minPrice, setMinPriceState])

  useEffect(() => {
    maxPrice && setMaxPriceState(maxPrice)
  }, [maxPrice, setMaxPriceState])

  const slowMinPrice = useDebouncedValue(minPriceState, 250)
  const slowMaxPrice = useDebouncedValue(maxPriceState, 250)

  useEffect(() => {
    setMinPrice(slowMinPrice)
  }, [slowMinPrice])

  useEffect(() => {
    setMaxPrice(slowMaxPrice)
  }, [slowMaxPrice])
  const convertSizeFilterName = (name: string) => {
    if (name === 'Size') return translate('w2.category.filter.size.title')
    if (name === 'Footwear Size') return translate('w2.category.filter.footwear.title')
    return name
  }
  const [isResetToMinPrice, setIsResetToMinPrice] = useState(false)
  const [isResetToMaxPrice, setIsResetToMaxPrice] = useState(false)

  const handleCheckBrand = useCallback(
    (manufacturer: ManufacturerFacet) => {
      toggleBrandFilter(manufacturer)
    },
    [toggleBrandFilter]
  )

  const renderManufacturerItem = useCallback(
    (manufacturer: ManufacturerFacet, index: number) => {
      return (
        <ManufacturerCheckItem
          key={manufacturer.id?.toString() ?? index.toString()}
          manufacturer={manufacturer}
          checked={isBrandFilterEnabled(manufacturer)}
          onCheck={handleCheckBrand.bind(this, manufacturer)}
        />
      )
    },
    [isBrandFilterEnabled, handleCheckBrand]
  )

  const slider = useMemo(
    () => (
      <RangeSlider
        resetMin={isResetToMinPrice}
        resetMax={isResetToMaxPrice}
        minAvailablePrice={`${minAvailablePrice}`}
        maxAvailablePrice={`${maxAvailablePrice}`}
        currentMaxPrice={currentMaxPrice}
        currentMinPrice={currentMinPrice}
        minValue={minPriceState ?? minAvailablePrice}
        maxValue={maxPriceState ?? maxAvailablePrice}
        onChange={({ min, max }) => {
          if (min === minAvailablePrice) {
            setMinPriceState(null)
          } else {
            setMinPriceState(min)
          }
          if (max === maxAvailablePrice) {
            setMaxPriceState(null)
          } else {
            setMaxPriceState(max)
          }

          setIsResetToMinPrice(min === minAvailablePrice)
          setIsResetToMaxPrice(max === maxAvailablePrice)
        }}
      />
    ),
    [
      minAvailablePrice,
      maxAvailablePrice,
      isResetToMinPrice,
      isResetToMaxPrice,
      minPriceState,
      maxPriceState,
      currentMaxPrice,
      currentMinPrice,
    ]
  )

  const [isBrandsSectionOpen, setIsBrandsSectionOpen] = useState(true)

  const handleCustomFacetCheck = (facet: CustomFacet, value: CustomFacetValue) => {
    toggleCustom(encodeCustomFilter(facet, value))
  }
  const openSection = (name: string, checked?: boolean) => {
    if (name === 'Gander') {
      return true
    }
    if (name === 'Size' && checked) {
      return true
    }

    return false
  }

  return (
    <CwsSection>
      <CwsGridRow>
        <CwsGridCol col="fluid">
          <CwsGridRow noGutter>
            <CwsGridCol col="12" className="cws-align-items-center ">
              <CwsH3 hideSpacing size="lead" className="cws-mb-24 flex items-center">
                <CwsIcon size="24px" icon="funnel" verticalAlign="middle" className="cws-mr-12" />
                {translate('filters.mobile.controls.filter')}
              </CwsH3>
              {/*<FilterHeading title={translate('filters.mobile.controls.filter')} icon="funnel" />*/}
              <CwsDivider variant="solid" />
            </CwsGridCol>

            {!isGloballyPricelist() && (
              <CwsGridCol col="12">
                <Section title={translate('filters.categories') as string}>
                  <CategoryList categories={categoryFacets} currentCategoryId={categoryId} />
                </Section>
                <SectionBreak />
              </CwsGridCol>
            )}

            {isGloballyPricelist() && !R.isEmpty(catalogGroups) && (
              <CwsGridCol col="12">
                <Section open={false} title={translate('w2.pricelist.ship.filter.heading') as string}>
                  <ShipFilter catalogGroups={catalogGroups} />
                </Section>
                <SectionBreak />
              </CwsGridCol>
            )}
            {!hideBrandsFilter && (
              <CwsGridCol col="12">
                <Section
                  title={translate('filters.manufacturers') as string}
                  open
                  onHeadingClick={setIsBrandsSectionOpen.bind(this, !isBrandsSectionOpen)}
                >
                  <CwsGridRow>
                    <CwsGridCol col="12">
                      <CwsLabelSet label={`${translate('nav.btn.search')}:`}>
                        <CwsInput
                          placeholder={searchBrandPlaceholder}
                          value={searchBrandValue}
                          onChange={(value: string) => setSearchBrandValue(value)}
                          prefix={<CwsIcon icon="search" size="24" />}
                        />
                      </CwsLabelSet>
                    </CwsGridCol>
                    {manufacturerFacetsToRender
                      .filter((m) => checkedBrands.includes(m.id.toString()))
                      .map(renderManufacturerItem)}
                    {manufacturerFacetsToRender
                      .filter((m) => !checkedBrands.includes(m.id.toString()))
                      .map(renderManufacturerItem)}
                    <CwsGridCol>
                      {manufacturerFacets.length > 12 && !searchBrandValue && (
                        <button
                          className="w-full"
                          onClick={setExpandedManufacturers.bind(this, !expandedManufacturers)}
                        >
                          <CwsP
                            className="flex justify-center items-center"
                            size="small"
                            weight="500"
                            color="buoy-dark"
                          >
                            {expandedManufacturers
                              ? translate('filters.brandname.show.less')
                              : translate('filters.brandname.show.all')}
                            <SpoilerChevron open={expandedManufacturers} />
                          </CwsP>
                        </button>
                      )}
                    </CwsGridCol>
                  </CwsGridRow>
                </Section>
                <SectionBreak />
              </CwsGridCol>
            )}
            <CwsGridCol col="12">
              {slider}
              <SectionBreak />
            </CwsGridCol>
            <CwsGridCol col="12">
              {customFacetsToRender
                ?.sort((a, b) => {
                  if (a.name && b.name) {
                    if (a.number === 10) return 1 // SHOP-3355 special sorting for Color
                    if (b.number === 10) return -1 // SHOP-3355 special sorting for Color
                    if (a.name === 'Color') return 2 // SHOP-3355 special sorting for Color
                    if (b.name === 'Color') return -2 // SHOP-3355 special sorting for Color

                    // todo: can the sort order come from KK
                    return a.name.localeCompare(b.name)
                  }
                  return 0
                })
                .map((facet) => {
                  if (!facet.values.length) return
                  return (
                    <>
                      <Section
                        key={facet.name}
                        open={openSection(facet.name, facet.values.length === 1)}
                        title={convertSizeFilterName(getFilterDescription(facet.number, facet.name))}
                      >
                        <CwsGridRow>
                          {facet?.values
                            .filter((value) => isCustomFilterEnabled(encodeCustomFilter(facet, value)))
                            .map((value) => {
                              const { name = '', number } = value
                              const translated = (translate(name) as string) || ''
                              return (
                                <FacetCheckItem
                                  key={name}
                                  name={facet.name === 'Size' ? translated : value.nameForSort}
                                  checked={true}
                                  onCheck={handleCustomFacetCheck.bind(this, facet, value)}
                                  count={number}
                                />
                              )
                            })}
                          {facet?.values
                            .filter((value) => !isCustomFilterEnabled(encodeCustomFilter(facet, value)))
                            .map((value) => {
                              const { name = '', number } = value
                              const translated = (translate(name) as string) || ''
                              return (
                                <FacetCheckItem
                                  key={name}
                                  name={facet.name === 'Size' ? translated : value.nameForSort}
                                  checked={false}
                                  onCheck={handleCustomFacetCheck.bind(this, facet, value)}
                                  count={number}
                                />
                              )
                            })}
                        </CwsGridRow>
                      </Section>
                      <SectionBreak />
                    </>
                  )
                })}
            </CwsGridCol>
            {isMobile && (
              <CwsGridCol className="cws-mt-24">
                <CwsButton width="fluid" onClick={() => modal.addEventModalHide('side-panel-filter-modal')}>
                  {translate('filters.mobile.controls.done')}
                </CwsButton>
              </CwsGridCol>
            )}

            <CwsGridCol col="12" className="cws-mt-24">
              <CwsButton width="fluid" iconLeft="refresh" variant="secondary" onClick={() => resetAll()}>
                {translate('filters.mobile.controls.reset')}
              </CwsButton>
            </CwsGridCol>
          </CwsGridRow>
        </CwsGridCol>
        {!isMobile && (
          <CwsGridCol col="auto">
            <CwsDivider height="100%" orientation="vertical" />
          </CwsGridCol>
        )}
      </CwsGridRow>
    </CwsSection>
  )
}

export default CategorySidePanel
